import { getXDateFormat } from 'App/Map/PanelDetails/StackemCharts/_utils/by-domain/_utils/getXDateFormat'
import { baseXRangeChartOptionsStacked } from 'components/StackedChart/_utils/baseXRangeChartOptionsStacked'
import type { HighchartsOptionsWithSemiosAdditions } from 'components/StackedChart/types'
import { merge } from 'highcharts/highstock'
import { translate } from 'i18n/i18n'
import { alphaColorReplacer, colors } from 'settings/colors'
import { v4 as uuid } from 'uuid'

// Base chart options for ICS Dashboard charts
const icsBaseChartOptions = (timezone: string): HighchartsOptionsWithSemiosAdditions => {
  return {
    semiosHighchartsAdditions: {
      id: `ics-chart-${uuid()}`,
      firstForecastTimestamp: Infinity,
    },
    accessibility: { enabled: false },
    navigator: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    chart: {
      animation: false,
      height: 250,
      marginLeft: 50,
      marginRight: 10,
      marginTop: 25,
      spacingBottom: 15,
      spacingTop: 15,
    },
    plotOptions: {
      series: {
        animation: false,
        states: {
          hover: {
            enabled: false,
          },
          inactive: {
            opacity: 1,
          },
        },
      },
    },
    legend: {
      enabled: false,
    },
    title: { text: '' },
    time: { timezone },
    tooltip: {
      xDateFormat: getXDateFormat(false),
      borderRadius: 8,
      borderWidth: 0,
      hideDelay: 0,
      outside: true,
      shadow: false,
      split: false,
      style: {
        pointerEvents: 'auto',
      },
    },
    xAxis: {
      type: 'datetime',
      ordinal: false,
      labels: {
        style: {
          color: colors.grey800,
          fontSize: '12px',
        },
      },
    },
    yAxis: {
      title: {
        text: '',
      },
      opposite: false,
    },
  }
}

// Chart type specific options for ICS Dashboard
const icsChartOptionsForType = (chartType: string, firstForecastTimestamp = Infinity) => {
  switch (true) {
    case ['line', 'spline', 'area', 'arearange', 'column'].includes(chartType):
      return {
        plotOptions: {
          series: {
            zoneAxis: 'x',
            zones: [
              {
                value: firstForecastTimestamp,
              },
              {
                dashStyle: 'Dash',
              },
            ],
          },
        },
      }

    case ['xrange'].includes(chartType):
      return baseXRangeChartOptionsStacked(firstForecastTimestamp)

    case ['heatmap'].includes(chartType):
      return {
        chart: {
          marginTop: 25,
          events: {
            load: function () {
              // @ts-expect-error - Highcharts chart context
              const seriesHasData = this.series.some((s) => {
                // @ts-expect-error - Highcharts data point
                return s.data.filter((d) => d.y !== null).length > 0
              })

              if (!seriesHasData) {
                // @ts-expect-error - Highcharts chart context
                this.update({
                  legend: { enabled: false },
                })
              } else {
                // @ts-expect-error - Highcharts chart context
                this.update({
                  legend: {
                    // @ts-expect-error - Highcharts chart context
                    symbolWidth: this.chartWidth - 100,
                  },
                })
              }
            },
          },
        },
        colorAxis: {
          labels: {
            align: 'center',
            overflow: 'allow',
          },
          reversed: false,
        },
        legend: {
          enabled: true,
        },
        noData: {
          position: {
            y: 20,
          },
        },
        plotOptions: {
          heatmap: {
            turboThreshold: 0,
            zoneAxis: 'x',
            zones: [
              {
                value: firstForecastTimestamp + 1000,
              },
              {
                dashStyle: 'Dot',
              },
            ],
          },
        },
        xAxis: {
          gridLineWidth: 1,
          gridZIndex: 4,
          labels: {
            enabled: false,
          },
          plotBands: [
            {
              color: alphaColorReplacer(colors.grey200, 0.25),
              from: firstForecastTimestamp,
              to: 999999999999999,
              zIndex: 1,
              label: {
                text: translate.phrases.banyanApp('Forecast'),
                y: -4,
                x: -10,
                align: 'right',
                style: {
                  fontSize: '14px',
                  zIndex: 99999999,
                },
                useHTML: true,
              },
            },
          ],
          tickInterval: 24 * 3600 * 1000,
          tickLength: 0,
          type: 'datetime',
        },
        yAxis: {
          endOnTick: false,
          gridLineWidth: 1,
          gridZIndex: 10,
          labels: {
            style: {
              fontSize: '9px',
            },
            y: 5,
          },
          min: 0,
          minorTickLength: 0,
          startOnTick: false,
          tickWidth: 1,
        },
      }

    case ['scatter'].includes(chartType):
      return {}

    default:
      throw new Error(`${chartType} not implemented`)
  }
}

// Simplified chart options maker for ICS Dashboard
// Removes chart syncing, custom margins, and x-axis extremes
export const buildIcsChartOptions = ({
  chartOptions,
  timezone,
}: {
  chartOptions: HighchartsOptionsWithSemiosAdditions
  timezone: string
}) => {
  const finalChartOptions: HighchartsOptionsWithSemiosAdditions = merge(
    icsBaseChartOptions(timezone),
    icsChartOptionsForType(
      chartOptions?.chart?.type ?? 'line',
      chartOptions.semiosHighchartsAdditions.firstForecastTimestamp,
    ),
    chartOptions,
  )

  return finalChartOptions
}
